import { useChatCoordinator } from 'app/ChatCoordinator';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import useAssignmentChat from 'hooks/chat/useAssignmentChat';
import { omit } from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import chatService from 'services/chat';
import feedbackService from 'services/feedback';
import submissionService from 'services/submission';
import { CHAT_QUERY_OPEN_VALUE, CHAT_QUERY_PARAM, hasUnreadChat } from 'utils/chat';
import { getAIPreview } from 'utils/helper';

const PageContext = createContext();

const buildSubmissionPath = ({ assignmentId, submissionId }) => {
    return `/assignments/${assignmentId}/submissions/${submissionId}`;
};

const createOpenedAssignmentChat = ({ assignmentId, courseId, ownerAccountId }) => {
    if (!ownerAccountId || !courseId || !assignmentId) return null;

    const timestamp = Date.now();

    return chatService.createChat({
        active: true,
        created_on: timestamp,
        modified_on: timestamp,
        first_opened_on: timestamp,
        owner_account_id: ownerAccountId,
        course_id: courseId,
        assignment_id: assignmentId,
        messages: [],
    });
};

const PageProvider = (props) => {
    const { course, currentAssignmentId, currentAssignment, studentSubmissions, setStudentSubmissions } = useApp();
    const { auth, getAccount } = useAuth();
    const { setOpenAssignmentChatId, setSubmissionPath } = useChatCoordinator();
    const { closeDialog, setDialog } = useDialog();
    const history = useHistory();
    const location = useLocation();
    const { submissionId } = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { onMessage } = useWebsocket();

    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [selectedInlineCommentId, setSelectedInlineCommentId] = useState(null);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [introductionDialog, setIntroductionDialog] = useState({ open: false });
    const isChatOpen = useMemo(() => {
        return new URLSearchParams(location.search).get(CHAT_QUERY_PARAM) === CHAT_QUERY_OPEN_VALUE;
    }, [location.search]);
    const { assignmentChat, setAssignmentChat } = useAssignmentChat({
        assignmentId: currentAssignmentId,
        courseId: auth?.course_id,
        ownerAccountId: auth?.id,
        reconciliationEnabled: !isChatOpen,
    });

    const currentSubmission = studentSubmissions?.find(({ id }) => id === submissionId);
    const currentSubmissionIndex = studentSubmissions
        ?.sort((a, b) => a.created_on - b.created_on)
        ?.findIndex(({ id }) => id === submissionId);

    const chatReaderAccountId = auth?.owner_account_id || auth?.id;
    const hasUnreadSubmissionChat = useMemo(() => {
        return hasUnreadChat({
            chat: assignmentChat,
            accountId: chatReaderAccountId,
        });
    }, [assignmentChat, chatReaderAccountId]);

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? []).map((item) => {
                return {
                    id: selectedFeedbackId,
                    value: item,
                };
            });
        };

        if (selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = currentSubmission?.ai?.feedback?.improvement_suggestions?.find(
                ({ id }) => id === selectedFeedbackId
            );
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        const comments = currentSubmission?.ai?.inline_comments ?? [];
        const activeHighlights = comments
            .filter(({ active }) => active)
            .filter(({ id }) => id === selectedInlineCommentId)
            .map(({ highlight }) => highlight);

        return format(activeHighlights);
    }, [
        currentSubmission?.ai?.feedback,
        currentSubmission?.ai?.inline_comments,
        selectedFeedbackId,
        selectedInlineCommentId,
    ]);

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}/submissions`);
    };

    const onClickInfo = () => {
        setIntroductionDialog({ open: true });
    };

    const onClickPrevAttempt = () => {
        const prevSubmissionId = studentSubmissions[currentSubmissionIndex - 1].id;
        history.push(`/assignments/${currentAssignmentId}/submissions/${prevSubmissionId}`);
    };

    const onClickNextAttempt = () => {
        const nextSubmissionId = studentSubmissions[currentSubmissionIndex + 1].id;
        history.push(`/assignments/${currentAssignmentId}/submissions/${nextSubmissionId}`);
    };

    const onClickRegenerate = () => {
        setDialog({
            open: true,
            message: t('dialog.regenerate-feedback.title', 'Regenerate Feedback?'),
            description: t(
                'dialog.regenerate-feedback.description',
                'This will permanently delete all existing feedback for this draft, and cannot be undone.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.regenerate', 'Regenerate'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setIsGenerating(true);
                try {
                    const account = await getAccount();
                    await getAIPreview({
                        target: 'feedback',
                        course_id: course.id,
                        owner_account_id: currentAssignment.owner_account_id,
                        assignment: {
                            ...currentAssignment,
                            ai_feedback_settings: {
                                ...currentAssignment.ai_feedback_settings,
                                ...currentAssignment?.ai_student_feedback_settings,
                            },
                        },
                        submission: currentSubmission,
                        connections: account.connections.map(({ id }) => id),
                        language: course.language ?? 'English',
                    });
                } catch (e) {
                    console.error(e);
                    message.error(
                        t('common:message.something-went-wrong-try-again', 'Something went wrong. Please try again.')
                    );
                }
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const updateChatQuery = useCallback(
        (open) => {
            const params = new URLSearchParams(location.search);

            if (open) {
                params.set(CHAT_QUERY_PARAM, CHAT_QUERY_OPEN_VALUE);
            } else {
                params.delete(CHAT_QUERY_PARAM);
            }

            const search = params.toString();

            history.push({
                pathname: location.pathname,
                search: search ? `?${search}` : '',
                hash: location.hash,
            });
        },
        [history, location.hash, location.pathname, location.search]
    );

    const onOpenChat = useCallback(() => {
        if (isChatOpen) return;
        updateChatQuery(true);
    }, [isChatOpen, updateChatQuery]);

    const resolveOpenedAssignmentChat = useCallback(async () => {
        try {
            return await createOpenedAssignmentChat({
                assignmentId: currentAssignmentId,
                courseId: auth?.course_id,
                ownerAccountId: auth?.id,
            });
        } catch (error) {
            console.error(error);
            return null;
        }
    }, [auth?.course_id, auth?.id, currentAssignmentId]);

    const onCloseChat = useCallback(() => {
        if (!isChatOpen) return;
        updateChatQuery(false);
    }, [isChatOpen, updateChatQuery]);

    const onAssignmentChatChange = useCallback(
        (chat) => {
            if (!chat) return;
            setAssignmentChat(chat);
        },
        [setAssignmentChat]
    );

    useEffect(() => {
        if (!currentAssignmentId || !submissionId) return;

        setSubmissionPath(
            currentAssignmentId,
            buildSubmissionPath({ assignmentId: currentAssignmentId, submissionId })
        );
    }, [currentAssignmentId, setSubmissionPath, submissionId]);

    useEffect(() => {
        setOpenAssignmentChatId(isChatOpen ? currentAssignmentId || null : null);

        return () => {
            setOpenAssignmentChatId((current) => (current === currentAssignmentId ? null : current));
        };
    }, [currentAssignmentId, isChatOpen, setOpenAssignmentChatId]);

    useEffect(() => {
        if (currentSubmission) {
            setSelectedFeedbackId(currentSubmission.ai.feedback?.improvement_suggestions?.[0]?.id);
        }
    }, [currentSubmission]);

    useEffect(() => {
        if (selectedFeedbackId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedFeedbackId]);

    useEffect(() => {
        if (selectedInlineCommentId !== null) {
            setSelectedFeedbackId(null);
        }
    }, [selectedInlineCommentId]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.data?.payload_type === 'regenerate_submission') {
                const { submission } = message.data.payload;
                const updatedSubmission = {
                    ...currentSubmission,
                    modified_on: new Date().getTime(),
                };
                if (isGenerating) {
                    updatedSubmission.ai.grading = submission.data.grading;
                    updatedSubmission.ai.feedback = submission.data.feedback;
                }
                setIsGenerating(false);
                setStudentSubmissions((current) => {
                    return current.map((submission) => {
                        if (submission.id !== updatedSubmission.id) return submission;
                        if (currentSubmission.is_legacy) {
                            feedbackService.updateFeedback(
                                updatedSubmission.id,
                                omit(
                                    {
                                        ...updatedSubmission,
                                        grading: updatedSubmission.grading,
                                        feedback: updatedSubmission.feedback,
                                        inline_comments: updatedSubmission.inline_comments,
                                    },
                                    ['_id', 'ai', 'type', 'is_legacy']
                                )
                            );
                        } else {
                            submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        }
                        return updatedSubmission;
                    });
                });
            }
        });
        return cleanup;
    }, [currentSubmission, isGenerating, onMessage, setStudentSubmissions]);

    return (
        <PageContext.Provider
            value={{
                selectedFeedbackId,
                setSelectedFeedbackId,
                selectedInlineCommentId,
                setSelectedInlineCommentId,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                isGenerating,
                setIsGenerating,
                introductionDialog,
                setIntroductionDialog,
                currentSubmission,
                currentSubmissionIndex,
                highlights: getHighlights(),
                hasUnreadSubmissionChat,
                isChatOpen,
                onClickBack,
                onClickInfo,
                onClickPrevAttempt,
                onClickNextAttempt,
                onClickRegenerate,
                onAssignmentChatChange,
                onCloseChat,
                onOpenChat,
                resolveOpenedAssignmentChat,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
